import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Clock, Rocket, Star, Zap, Bell, Mail } from 'lucide-react';

const ComingSoon = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Get feature name from route or state
  const featureName = location.state?.featureName || 'This Feature';
  const description = location.state?.description || 'This exciting feature is currently under development and will be available soon!';
  const estimatedDate = location.state?.estimatedDate || new Date('2025-03-01');

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = new Date(estimatedDate).getTime() - now;

      setCountdown({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [estimatedDate]);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setTimeout(() => {
        setEmail('');
        setSubscribed(false);
      }, 3000);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px',
      position: 'relative',
      overflow: 'hidden',
    },
    stars: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
    },
    star: {
      position: 'absolute',
      backgroundColor: 'white',
      borderRadius: '50%',
      animation: 'twinkle 3s infinite',
    },
    contentWrapper: {
      backgroundColor: '#ffffff',
      borderRadius: '24px',
      padding: '60px 40px',
      maxWidth: '700px',
      width: '100%',
      textAlign: 'center',
      position: 'relative',
      zIndex: 1,
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    },
    backButton: {
      position: 'absolute',
      top: '20px',
      left: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      backgroundColor: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      color: '#64748b',
      transition: 'all 0.2s',
    },
    iconWrapper: {
      marginBottom: '24px',
      display: 'flex',
      justifyContent: 'center',
      gap: '16px',
    },
    icon: {
      animation: 'bounce 2s infinite',
    },
    title: {
      fontSize: '36px',
      fontWeight: 'bold',
      color: '#1e293b',
      marginBottom: '16px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    featureName: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#667eea',
      marginBottom: '16px',
    },
    description: {
      fontSize: '16px',
      color: '#64748b',
      marginBottom: '40px',
      lineHeight: '1.6',
    },
    countdownContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '16px',
      marginBottom: '40px',
    },
    countdownBox: {
      backgroundColor: '#f8fafc',
      padding: '24px 16px',
      borderRadius: '12px',
      border: '2px solid #e2e8f0',
    },
    countdownNumber: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#667eea',
      display: 'block',
      marginBottom: '8px',
    },
    countdownLabel: {
      fontSize: '14px',
      color: '#64748b',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    featuresBox: {
      backgroundColor: '#f8fafc',
      padding: '24px',
      borderRadius: '12px',
      marginBottom: '32px',
      border: '1px solid #e2e8f0',
    },
    featuresTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    featuresList: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px',
      textAlign: 'left',
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#64748b',
      fontSize: '14px',
    },
    checkIcon: {
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      backgroundColor: '#667eea',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    subscribeBox: {
      marginBottom: '24px',
    },
    subscribeTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    subscribeForm: {
      display: 'flex',
      gap: '12px',
      maxWidth: '400px',
      margin: '0 auto',
    },
    input: {
      flex: 1,
      padding: '12px 16px',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
    },
    subscribeButton: {
      padding: '12px 24px',
      backgroundColor: '#667eea',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap',
    },
    successMessage: {
      marginTop: '12px',
      padding: '12px',
      backgroundColor: '#dcfce7',
      color: '#166534',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
    },
    progressBar: {
      width: '100%',
      height: '8px',
      backgroundColor: '#e2e8f0',
      borderRadius: '4px',
      overflow: 'hidden',
      marginBottom: '12px',
    },
    progressFill: {
      height: '100%',
      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
      width: '65%',
      animation: 'progress 2s ease-in-out infinite',
    },
    progressText: {
      fontSize: '12px',
      color: '#64748b',
      marginBottom: '24px',
    },
  };

  // Add CSS animations
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = `
      @keyframes bounce {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
      
      @keyframes twinkle {
        0%, 100% { opacity: 0.3; }
        50% { opacity: 1; }
      }
      
      @keyframes progress {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(400%); }
      }

      @media (max-width: 640px) {
        .countdown-container { grid-template-columns: repeat(2, 1fr) !important; }
        .features-list { grid-template-columns: 1fr !important; }
        .subscribe-form { flex-direction: column !important; }
      }
    `;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  // Generate random stars
  const generateStars = () => {
    const stars = [];
    for (let i = 0; i < 50; i++) {
      stars.push(
        <div
          key={i}
          style={{
            ...styles.star,
            width: Math.random() * 3 + 'px',
            height: Math.random() * 3 + 'px',
            top: Math.random() * 100 + '%',
            left: Math.random() * 100 + '%',
            animationDelay: Math.random() * 3 + 's',
          }}
        />
      );
    }
    return stars;
  };

  return (
    <div style={styles.container}>
      <div style={styles.stars}>{generateStars()}</div>

      <div style={styles.contentWrapper}>
        <button
          style={styles.backButton}
          onClick={() => navigate(-1)}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#e2e8f0';
            e.currentTarget.style.transform = 'translateX(-4px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#f8fafc';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <div style={styles.iconWrapper}>
          <Rocket size={48} color="#667eea" style={styles.icon} />
          <Star size={48} color="#f59e0b" style={{ ...styles.icon, animationDelay: '0.2s' }} />
          <Zap size={48} color="#667eea" style={{ ...styles.icon, animationDelay: '0.4s' }} />
        </div>

        <h1 style={styles.title}>Coming Soon!</h1>
        <h2 style={styles.featureName}>{featureName}</h2>
        <p style={styles.description}>{description}</p>

        <div style={styles.progressBar}>
          <div style={styles.progressFill} />
        </div>
        <p style={styles.progressText}>Development Progress: 65% Complete</p>

        <div className="countdown-container" style={styles.countdownContainer}>
          <div style={styles.countdownBox}>
            <span style={styles.countdownNumber}>{countdown.days}</span>
            <span style={styles.countdownLabel}>Days</span>
          </div>
          <div style={styles.countdownBox}>
            <span style={styles.countdownNumber}>{countdown.hours}</span>
            <span style={styles.countdownLabel}>Hours</span>
          </div>
          <div style={styles.countdownBox}>
            <span style={styles.countdownNumber}>{countdown.minutes}</span>
            <span style={styles.countdownLabel}>Minutes</span>
          </div>
          <div style={styles.countdownBox}>
            <span style={styles.countdownNumber}>{countdown.seconds}</span>
            <span style={styles.countdownLabel}>Seconds</span>
          </div>
        </div>

        <div style={styles.featuresBox}>
          <div style={styles.featuresTitle}>
            <Clock size={20} />
            What's Coming:
          </div>
          <div className="features-list" style={styles.featuresList}>
            <div style={styles.featureItem}>
              <div style={styles.checkIcon}>✓</div>
              Advanced Analytics
            </div>
            <div style={styles.featureItem}>
              <div style={styles.checkIcon}>✓</div>
              Real-time Updates
            </div>
            <div style={styles.featureItem}>
              <div style={styles.checkIcon}>✓</div>
              Enhanced UI/UX
            </div>
            <div style={styles.featureItem}>
              <div style={styles.checkIcon}>✓</div>
              Mobile Support
            </div>
          </div>
        </div>

        <div style={styles.subscribeBox}>
          <div style={styles.subscribeTitle}>
            <Bell size={20} />
            Get Notified When We Launch
          </div>
          <form onSubmit={handleSubscribe} className="subscribe-form" style={styles.subscribeForm}>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              required
              onFocus={(e) => e.currentTarget.style.borderColor = '#667eea'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
            />
            <button
              type="submit"
              style={styles.subscribeButton}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5568d3'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#667eea'}
            >
              <Mail size={16} style={{ display: 'inline', marginRight: '4px' }} />
              Notify Me
            </button>
          </form>
          {subscribed && (
            <div style={styles.successMessage}>
              ✓ Thank you! We'll notify you when this feature is ready.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComingSoon;